import { Box, Grid, Paper } from '@mui/material';
import { selectedClinicsVar } from 'apollo/reactive-variables/invoice';
import { ClinicDropdown } from 'models/user';
import React, { FC, useState } from 'react';
import CollectiveInvoicesList from '../collective-invoices-list/collective-invoices-list.component';
import CollectiveInvoicesPageAction from '../collective-invoices-page-action/collective-invoices-page-action.component';
import { Period } from 'models/organization';

type ManageCollectiveInvoicesProps = {
    updateClinicsLength: (length: number) => void;
};

const ManageCollectiveInvoices: FC<ManageCollectiveInvoicesProps> = ({
    updateClinicsLength,
}: ManageCollectiveInvoicesProps) => {
    const [selectedClinics, setSelectedClinics] = useState<{ [key: string]: string }>(selectedClinicsVar());
    const periodMap: Record<keyof Period, string> = {
        monthly: 'MONTHLY',
        semiWeekly: 'SEMI_WEEKLY',
        weekly: 'WEEKLY',
    };

    const clinicPickHandler = (clinic: ClinicDropdown) => {
        const selectedClinicNames = { ...selectedClinics };
        let newState: { [key: string]: string };
        if (selectedClinicNames[clinic._id]) {
            delete selectedClinicNames[clinic._id];
            newState = Object.assign({}, selectedClinicNames);
        } else {
            newState = Object.assign({}, selectedClinicNames, { [clinic._id]: clinic.name });
        }
        setSelectedClinics(newState);
        selectedClinicsVar(newState);
        updateClinicsLength(Object.keys(newState).length);
    };

    const allClinicsHandler = (clinics: ClinicDropdown[], selectedPeriods: Period) => {
        const allFalse = Object.values(selectedPeriods).every((val) => !val);

        const selectedEnumValues = Object.entries(selectedPeriods)
            .filter(([, isSelected]) => isSelected)
            .map(([key]) => periodMap[key as keyof typeof periodMap]);

        const filtered = allFalse
            ? clinics
            : clinics.filter((clinic) => selectedEnumValues.includes(clinic.invoicePeriod as string));

        const sClinics: { [key: string]: string } = {};
        filtered.forEach((clinic: ClinicDropdown) => {
            sClinics[clinic._id] = clinic.name;
        });

        setSelectedClinics(sClinics);
        selectedClinicsVar(sClinics);
        updateClinicsLength(Object.keys(sClinics).length);
    };

    const handlePeriodChangeFromChild = (updatedPeriods: Period, allClinics: ClinicDropdown[]) => {
        const allFalse = Object.values(updatedPeriods).every((val) => !val);
        const selectedPeriodEnums = Object.entries(updatedPeriods)
            .filter(([, isSelected]) => isSelected)
            .map(([key]) => periodMap[key as keyof typeof periodMap]);

        const selectedClinicIds = allClinics
            .filter((clinic) => allFalse || selectedPeriodEnums.includes(clinic.invoicePeriod as string))
            .map((clinic) => clinic._id);

        const filtered = Object.entries(selectedClinics).filter(([clinicId]) => selectedClinicIds.includes(clinicId));

        const sClinics = Object.fromEntries(filtered.map(([clinicId, clinicName]) => [clinicId, clinicName]));

        setSelectedClinics(sClinics);
        selectedClinicsVar(sClinics);
        updateClinicsLength(Object.keys(sClinics).length);
    };

    const handleClinicRemove = (clinicId: string) => {
        const sClinics = { ...selectedClinics };
        delete sClinics[clinicId];
        setSelectedClinics(sClinics);
        selectedClinicsVar(sClinics);
        updateClinicsLength(Object.keys(sClinics).length);
    };

    return (
        <Paper variant="outlined">
            <Box p={2}>
                <Grid container spacing={2}>
                    <CollectiveInvoicesPageAction
                        onClinicPickChange={clinicPickHandler}
                        onAllClinicsPickChange={allClinicsHandler}
                        selectedClinics={selectedClinics}
                        onPeriodChange={handlePeriodChangeFromChild}
                    />
                    <CollectiveInvoicesList onClinicRemove={handleClinicRemove} selectedClinics={selectedClinics} />
                </Grid>
            </Box>
        </Paper>
    );
};

export default ManageCollectiveInvoices;
